import { type ReactElement, type Dispatch, type SetStateAction, useState, createContext, useEffect } from 'react'
import { Box, Typography, Link as MuiLink, Skeleton } from '@mui/material'
import { useRouter } from 'next/router'
import NextLink from 'next/link'
import useInheritance from '@/hooks/useInheritance'
// import ModalButton from '@/components/common/ModalButton'
import CardContainer from '@/components/dashboard/CardContainer'
// import ClaimableAssets from '@/components/dashboard/Assets/ClaimableAssets'
// import SendProcessing from '@/components/inheritors/SendProcessing'
import { Body2White } from '@/components/typography'
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
// import EthHashInfo from '@/components/common/EthHashInfo'
import useDesktop from '@/hooks/useDesktop'
// import useInheritanceOfInheritor from '@/hooks/useInheritanceOfInheritor'
import InheritanceOfInheritor from '@/components/dashboard/NoticeInheritance/InheritanceOfInheritor'
import useLoadInheritanceOfInheritor from '@/hooks/loadables/useLoadInheritanceOfInheritor'
import { RuleType } from '@/store/setUpInheritorsSlice'
import RuleRender from '@/components/inheritors/RuleRender'
import { INHERIT_EVENTS, trackEvent } from '@/services/analytics'

export const NoticeInheritanceContext = createContext<{
  setRefreshInheritor: Dispatch<SetStateAction<number>>
}>({
  setRefreshInheritor: () => {},
})

const NoticeInheritance = (): ReactElement => {
  const router = useRouter()
  const isDesktop = useDesktop()
  const [refreshInheritor, setRefreshInheritor] = useState(0)

  useEffect(() => {
    // if (systemCounter === 0) return

    const timerId = setTimeout(() => setRefreshInheritor(refreshInheritor + 1), 10e3)
    return () => clearTimeout(timerId)
  }, [refreshInheritor])

  const inheritance = useInheritance()
  const notSetupInheritance = inheritance.rule.type === RuleType.Unknown

  const [data, error, loading] = useLoadInheritanceOfInheritor(refreshInheritor)

  const hasInheritance = data && data.length > 0

  if (loading && !data) {
    return (
      <CardContainer title={'Notice of Inheritance'}>
        <Box px={'16px'} flex={1} sx={{ width: '100%' }}>
          <Skeleton variant="text" width="10em" sx={{ mb: 1 }} />
          <Skeleton width="100%" height={40} sx={{ mb: '6px' }} variant="rounded" />
        </Box>
      </CardContainer>
    )
  }

  if (hasInheritance) {
    return (
      <NoticeInheritanceContext.Provider value={{ setRefreshInheritor }}>
        {data && <InheritanceOfInheritor inheritances={data} />}
      </NoticeInheritanceContext.Provider>
    )
  }

  return (
    <CardContainer title={'Notice of Inheritance'}>
      <>
        {notSetupInheritance && (
          <Box px={'16px'} flex={1} sx={{ width: '100%' }}>
            <Typography
              display="inline"
              sx={{
                color: 'var(--Ebony-Clay-400)',
                fontSize: '14px',
                fontWeight: '400',
                fontStyle: 'normal',
                lineHeight: '160%',
              }}
              variant="body1"
              component="h2"
            >
              Secure Your Future - ensure your assets are passed on seamlessly to your inheritors by creating rules for
              automatic transfers.{' '}
            </Typography>
            <Typography
              display="inline"
              sx={{
                color: 'var(--Emerald-600)',
                fontSize: '14px',
                fontWeight: 'bold',
                fontStyle: 'normal',
                lineHeight: '160%',
              }}
              variant="body1"
            >
              {' '}
              <MuiLink
                href={{
                  pathname: 'set-up-inheritance',
                  query: { wallet: router.query.wallet },
                }}
                component={NextLink}
                variant="body2"
                sx={{
                  '&:hover': {
                    color: 'var(--Emerald-500)',
                  },
                }}
                onClick={() =>
                  trackEvent({ ...INHERIT_EVENTS.CLICK_SET_UP_INHERITANCE, ...{ label: 'Set up inheritance' } })
                }
              >
                Set up inheritance!
              </MuiLink>
            </Typography>
          </Box>
        )}
        {!notSetupInheritance && (
          <Box px={'16px'} flex={1} sx={{ width: '100%' }}>
            <Typography
              display="inline"
              sx={{
                color: 'var(--Ebony-Clay-400)',
                fontSize: '14px',
                fontWeight: '400',
                fontStyle: 'normal',
                lineHeight: '160%',
              }}
              variant="body1"
              component="h2"
            >
              Congratulations on setting up your inheritance!
            </Typography>
            <Box
              sx={{
                marginTop: '8px',
                border: '1px solid var(--Ebony-Clay-950, #2F354B)',
                borderRadius: '4px',
                paddingX: '16px',
                paddingY: '8px',
                // background: 'var(--Supporting-Dark-BG-700, #272A3E)',
              }}
            >
              <RuleRender
                rule={inheritance.rule}
                numInheritors={inheritance.inheritors.length}
                color={'var(--Blue-Ribbon-600, #5D5DFF)'}
                showTag
                lastActivity={inheritance.lastActivity}
                numClaimed={inheritance.claimed}
              />
              <Body2White sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)', mt: '4px' }}>
                {highlightPurpleText(`${inheritance.allocated / 100}%`)} allocated to inheritors.
              </Body2White>
            </Box>

            <Typography
              sx={{
                color: 'var(--Emerald-600)',
                fontSize: '14px',
                fontWeight: 'bold',
                fontStyle: 'normal',
                lineHeight: '160%',
                marginTop: '8px',
              }}
              variant="body1"
            >
              {' '}
              <MuiLink
                href={{
                  pathname: 'inheritance-management',
                  query: { wallet: router.query.wallet },
                }}
                component={NextLink}
                variant="body2"
                sx={{
                  '&:hover': {
                    color: 'var(--Emerald-500)',
                  },
                }}
                onClick={() =>
                  trackEvent({ ...INHERIT_EVENTS.CLICK_SET_UP_INHERITANCE, ...{ label: 'Manage inheritance' } })
                }
              >
                Manage inheritance!
              </MuiLink>
            </Typography>
          </Box>
        )}
      </>
    </CardContainer>
  )
}

const highlightPurpleText = (text: string) => {
  return <span style={{ color: 'var(--Blue-Ribbon-600, #5D5DFF)', fontWeight: 'bold' }}>{text}</span>
}

export default NoticeInheritance
